<!-- Vue component -->
<template>
	<div
		class="multi-select__w"
		:class="{
			'-t-dark': darkTheme,
			'-rounded': rounded,
		}"
	>
		<InputLabel
			v-if="label"
			:required="required"
			:text="label"
			:info="info"
			:hide-optional="hideOptional"
			:dark-theme="darkTheme"
		/>
		<div
			v-if="!selectedInline && selectedValue.length > 0"
			class="multi-select__tag-w"
		>
			<draggable
				v-if="sortable"
				v-model="selectedValue"
				tag="div"
				class="multi-select__tag-sortable-list"
			>
				<span
					v-for="v in selectedValue"
					:key="v.value"
					class="multi-select__tag"
					:class="{'is-disabled': v.$isDisabled, '-draggable': true }"
				>
					<span class="multi-select__tag-text">
						{{ v.label }}
					</span>
					<Icon
						v-if="!v.$isDisabled"
						icon-name="e-remove"
						size="sm"
						class="multi-select__tag-icon"
						@click="removeOption(v)"
					/>
				</span>
			</draggable>
			<span
				v-for="v in selectedValue"
				v-else
				:key="v.value"
				class="multi-select__tag"
				:class="{'is-disabled': v.$isDisabled }"
			>
				<span class="multi-select__tag-text">
					{{ v.label }}
				</span>
				<Icon
					v-if="!v.$isDisabled"
					icon-name="e-remove"
					size="sm"
					class="multi-select__tag-icon"
					@click="removeOption(v)"
				/>
			</span>
		</div>
		<multiselect
			ref="multiselect"
			:value="selectedValue"
			:options="options"
			:multiple="true"
			track-by="value"
			label="label"
			:group-values="isGroupSelect ? 'options' : null"
			:group-label="isGroupSelect ? 'label' : null"
			:group-select="isGroupSelect"
			:close-on-select="false"
			:placeholder="(searchable && isOpen) ? `${$Helpers.ucfirst($I18n.trans('commons.search'))}...` : placeholder || `${$I18n.trans('commons.select')}...`"
			:limit="999"
			:searchable="searchable"
			:show-labels="false"
			:limit-text="count => `+${count}`"
			:disabled="disabled"
			class="multi-select"
			:class="{
				'has-selection': selectedValue.length > 0,
				'-group-select': isGroupSelect,
				'-select-inline': selectedInline,
				'-no-search': !searchable,
				'-with-subtitles': withSubtitleOptions,
				'-rounded': rounded,
			}"
			@input="changeSelect"
			@open="isOpen = true"
			@close="isOpen = false"
			@search-change="(value) => $emit('search-change', value)"
		>
			<template #clear>
				<span
					v-if="selectedInline && selectedValue.length > 0"
					class="multi-select__clear"
					@click="clearSelection"
				>
					<span class="multi-select__clear-count">
						{{ selectedValue.length }}
					</span>
					<Icon
						v-if="selectedValue.length > 0"
						icon-name="e-remove"
						size="sm"
					/>
				</span>
			</template>
			<template #caret="{ attributes }">
				<span
					v-bind="attributes"
					class="multi-select__toggle-icon"
				>
					<Icon icon-name="down-arrow" />
				</span>
			</template>
			<template
				v-if="selectedInline"
				#tag="{ option }"
			>
				{{ option.label }}{{ option.value !== selectedValue[selectedValue.length - 1].value ? ',': '' }}
			</template>
			<template
				v-else
				#tag="{ option }"
			>
				<span class="multi-select__value">{{ option.label }}</span>
			</template>
			<template #beforeList>
				<div
					v-if="customAction && customActionLabel"
					class="multi-select__custom-action"
					@click="customAction"
				>
					<Icon
						v-if="customActionIcon"
						class="multi-select__custom-action-icon"
						:icon-name="customActionIcon"
						size="sm"
					/>
					{{ customActionLabel }}
				</div>
				<div
					v-if="optionsCount > 0 && enableToggleAll"
					class="multi-select__select-all"
					:class="{'is-selected': selectedValue.length === optionsCount}"
					@click="toggleAll"
				>
					{{ $I18n.trans('commons.select_all') }}
				</div>
			</template>
			<template #noResult>
				<div class="multi-select__no-results">
					{{ $I18n.trans('commons.no_results') }}
				</div>
			</template>
			<template #noOptions>
				<div class="multi-select__no-results">
					{{ $I18n.trans('commons.no_results') }}
				</div>
			</template>

			<template #option="props">
				<div class="multi-select__selected-option-text-w">
					<div class="multi-select__selected-option-title">
						{{ props.option.$groupLabel || props.option.label }}
					</div>
					<div
						v-if="props.option.subtitle"
						class="multi-select__selected-option-subtitle"
					>
						{{ props.option.subtitle }}
					</div>
				</div>
			</template>
		</multiselect>
	</div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Icon from '@shared/components/Icon';
import InputLabel from '@shared/components/Form/InputLabel';
import draggable from 'vuedraggable';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
	name:       'MultiSelect',
	components: { Multiselect, Icon, InputLabel, draggable },
	props:      {
		value: {
			type:    Array,
			default: () => [],
		},
		options: {
			type:     Array,
			required: true,
		},
		label: {
			type:    String,
			default: null,
		},
		info: {
			type:    String,
			default: null,
		},
		required: {
			type:    Boolean,
			default: false,
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		placeholder: {
			type:    String,
			default: null,
		},
		searchable: {
			type:    Boolean,
			default: true,
		},
		enableToggleAll: {
			type:    Boolean,
			default: true,
		},
		selectedInline: {
			type:    Boolean,
			default: true,
		},
		sortable: {
			type:    Boolean,
			default: false,
		},
		customAction: {
			type:    Function,
			default: null,
		},
		customActionLabel: {
			type:    String,
			default: null,
		},
		customActionIcon: {
			type:    String,
			default: null,
		},
		hideOptional: {
			type:    Boolean,
			default: false,
		},
		darkTheme: {
			type:    Boolean,
			default: false,
		},
		rounded: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			selectedValue: this.value || [],
			isOpen:        false,
		};
	},
	computed: {
		optionsCount() {
			if (this.isGroupSelect) {
				return this.options.reduce((acc, val) => acc + (val.options?.length || 0), 0);
			} else {
				return this.options.length;
			}
		},
		isGroupSelect() {
			return this.options.some(option => option.options);
		},
		withSubtitleOptions() {
			return this.options.every(option => {
				return Object.prototype.hasOwnProperty.call(option, 'label') && Object.prototype.hasOwnProperty.call(option, 'subtitle');
			});
		},
	},
	watch: {
		value() {
			this.selectedValue = this.value;
		},
		selectedValue(newValue) {
			if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
				this.$emit('change', newValue);
			}
		},
		isOpen(newValue) {
			this.$emit('open-change', newValue);
		},
	},
	methods: {
		changeSelect(option) {
			this.selectedValue = option;
		},
		toggleAll() {
			if (this.isGroupSelect) {
				if (this.selectedValue.length < this.optionsCount) {
					const newSelectedValues = [];
					for (const item of this.options) {
						for (const child of item.options) {
							newSelectedValues.push(child);
						}
					}
					this.selectedValue = newSelectedValues;
				} else {
					this.selectedValue = [];
				}
			} else {
				if (this.selectedValue.length < this.optionsCount) {
					this.selectedValue = [...this.options].sort((a, _b) => a.$isDisabled ? -1 : 1);
				} else {
					this.selectedValue = this.selectedValue.filter(v => v.$isDisabled);
				}
			}
		},
		clearSelection() {
			this.selectedValue = [];
		},
		removeOption(removedOption) {
			this.selectedValue = this.selectedValue.filter(option => option.value !== removedOption.value);
		},
	},
};
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import '@shared/sass/shared-variables';

$multi-select-clear-width: 60px;
$multi-select-clear-height: 28px;
$multi-select-indent: 43px;
$multi-select-nested-indent: 51px;

////////////////////////
// Custom elements
////////////////////////

/* stylelint-disable max-nesting-depth, selector-max-compound-selectors */

.multi-select__w {
	// Colors
	--ms-colors--lightest: var(--color-grey-200);
	--ms-colors--light: var(--color-grey-400);
	--ms-colors--dark: var(--color-grey-400);
	--ms-colors--darkest: var(--color-grey-500);

	// Input
	--ms-input-color: #{$form-color-input-text};
	--ms-input-bg: #{$form-color-bg};
	--ms-input-placeholder-color: #{$form-color-input-text-disabled};
	--ms-input-border-color: #{$form-color-border};
	--ms-input-border-color-hover: #{$form-color-border-hover};
	--ms-input-border-radius: var(--t-form-border-radius);

	// Disabled State
	--ms-disabled-bg: #{$form-color-bg-disabled};
	--ms-disabled-color: #{$form-color-input-text-disabled};
	--ms-disabled-border-color: #{$form-color-border};

	// Dropdown
	--ms-dropdown-bg: var(--color-white);
	--ms-dropdown-color: #{$form-color-input-text};
	--ms-dropdown-box-shadow: #{$box-shadow-card};

	// Options
	--ms-option-color: #{$form-color-input-text};
	--ms-option-bg-hover: #{$form-color-selection-hover};
	--ms-option-border-color: var(--color-grey-600);
	--ms-option-selected-color: var(--color-green-petrol);
	--ms-option-group-border-color: var(--color-grey-100);
	--ms-option-selected-bg-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M1.5 13.5l6 6 15-15" stroke="white" stroke-miterlimit="10" stroke-linejoin="round" fill="none"></path> </svg>');
	--ms-option-color-checkmark: var(--color-white);

	// Tags
	--ms-tag-bg: var(--color-green-petrol);
	--ms-tag-color: var(--color-white);
	--ms-tag-disabled-bg: var(--color-grey-300);

	// Custom Action
	--ms-custom-action-color: var(--color-green-petrol);
	--ms-custom-action-bg-hover: #{$form-color-selection-hover};

	// Subtitle
	--ms-subtitle-color: var(--color-grey-300);

	margin-bottom: $form-margin-bottom;

	&.-t-dark {
		// Input
		--ms-input-color: #{$form-color-input-text--dark};
		--ms-input-bg: #{$form-color-bg--dark};
		--ms-input-placeholder-color: #{$form-color-input-placeholder};
		--ms-input-border-color: #{$form-color-border--dark};
		--ms-input-border-color-hover: #{$form-color-border-hover--dark};

		// Disabled State
		--ms-disabled-bg: #{$form-color-bg-disabled--dark};
		--ms-disabled-color: #{$form-color-input-text-disabled--dark};
		--ms-disabled-border-color: #{$form-color-border--dark};

		// Dropdown
		--ms-dropdown-bg: #{$form-color-bg-focus--dark};
		--ms-dropdown-color: #{$form-color-input-text--dark};
		--ms-dropdown-box-shadow: none;

		// Options
		--ms-option-color: #{$form-color-input-text--dark};
		--ms-option-bg-hover: #{$form-color-selection-hover--dark};
		--ms-option-border-color: var(--color-grey-600);
		--ms-option-selected-color: var(--color-hype-yellow);
		--ms-option-group-border-color: var(--color-grey-100);
		--ms-option-selected-bg-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M1.5 13.5l6 6 15-15" stroke="black" stroke-miterlimit="10" stroke-linejoin="round" fill="none"></path> </svg>');
		--ms-option-color-checkmark: var(--color-text-on-hype-yellow);

		// Tags
		--ms-tag-bg: var(--color-hype-yellow);
		--ms-tag-color: var(--color-text-on-hype-yellow);
		--ms-tag-disabled-bg: var(--color-grey-300);

		// Custom Action
		--ms-custom-action-color: #{$form-color-input-text--dark};
		--ms-custom-action-bg-hover: #{$form-color-selection-hover--dark};

		// Subtitle
		--ms-subtitle-color: var(--color-grey-300);
	}

	&.-rounded {
		--ms-input-border-radius: var(--t-form-border-radius-rounded);
	}
}

.multi-select {
	&:hover {
		cursor: text;
	}
}

.multi-select__value {
	@include visually-hidden;
}

.multi-select__clear {
	position: absolute;
	top: 50%;
	width: $multi-select-clear-width;
	height: $multi-select-clear-height;
	transform: translateY(-50%);
	left: $form-input-padding;
	color: var(--ms-tag-color);
	background-color: var(--ms-tag-bg);
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding-left: $sp3;
	padding-right: $sp3;
	border-radius: math.div($multi-select-clear-height, 2); // stylelint-disable-line sh-waqar/declaration-use-variable
	cursor: pointer;
}

.multi-select__clear-count {
	@include font-size(fs-80);
	flex-grow: 1;
	text-align: center;
}

.multi-select__toggle-icon {
	position: absolute;
	right: $form-input-padding;
	top: 50%;
	transform: translateY(-50%);
	line-height: 0;
	z-index: 2;
	transition: transform $trans-time-fast;

	.multiselect--active & {
		transform: translateY(-50%) rotate(-180deg);
		transform-origin: center;
	}
}

.multi-select__select-all {
	position: relative;
	height: $form-input-height;
	padding-left: $multi-select-indent;
	padding-right: $form-input-padding;
	display: flex;
	align-items: center;

	&::before,
	&::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 13px;
		width: 22px;
		height: 22px;
		transform: translateY(-50%);
	}

	&::before {
		padding: 4px;
	}

	&::after {
		border: 1px solid var(--ms-option-border-color);
	}

	&:hover {
		background-color: var(--ms-option-bg-hover);
		cursor: pointer;
	}

	&.is-selected {
		&::before {
			content: '✓';
			font-family: monospace; // stylelint-disable-line
			display: inline-flex;
			align-items: center;
			justify-content: center;
			color: var(--ms-option-color-checkmark);
			background-color: var(--ms-option-selected-color);
		}

		&::after {
			border: 1px solid var(--ms-option-selected-color);
		}
	}
}

.multi-select__tag-w {
	display: flex;
	flex-wrap: wrap;
	gap: $sp3;
	margin-bottom: $sp3;
}

.multi-select__tag-sortable-list {
	display: flex;
	flex-wrap: wrap;
	gap: $sp3;
}

.multi-select__tag {
	position: relative;
	min-height: $multi-select-clear-height;
	color: var(--ms-tag-color);
	background-color: var(--ms-tag-bg);
	width: auto;
	max-width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding-left: $sp3;
	padding-right: $sp3;
	border-radius: math.div($multi-select-clear-height, 2); // stylelint-disable-line sh-waqar/declaration-use-variable

	&.-draggable {
		cursor: move;
	}

	&.is-disabled {
		background-color: var(--ms-tag-disabled-bg);
	}
}

.multi-select__tag-icon {
	cursor: pointer;
}

.multi-select__tag-text {
	@include font-size(fs-80);
	min-width: 0;
	margin-right: $sp2;
	user-select: none;
	white-space: normal;
	overflow-wrap: anywhere;
}

.multi-select__no-results {
	text-align: center;
}

.multi-select__custom-action {
	display: flex;
	align-items: center;
	padding: 0 $form-input-padding;
	width: 100%;
	height: $form-input-height;
	min-height: auto;
	color: var(--ms-custom-action-color);
	line-height: $form-input-height;

	&:hover {
		background-color: var(--ms-custom-action-bg-hover);
		cursor: pointer;
	}
}

.multi-select__custom-action-icon {
	margin-right: $sp2;
}

.multi-select__selected-option-text-w {
	margin-top: $form-input-padding;
	margin-bottom: $form-input-padding;
	min-width: 0; // fix for overflow ellipsis
}

.multi-select__selected-option-title {
	@include form-input-text-font();
	display: block;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin: 0;
	line-height: 120%;
}

.multi-select__selected-option-subtitle {
	@include font(primary, book, normal, fs-80);
	color: var(--ms-subtitle-color);
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-top: $sp2;
}

::v-deep {
	.multiselect {
		@include form-input-text-font();
		min-height: $form-input-height;
		color: var(--ms-input-color);

		&.multiselect--disabled {
			border-radius: var(--ms-input-border-radius);
			cursor: not-allowed;
			pointer-events: initial;
		}
	}

	.multiselect__placeholder {
		@include form-input-placeholder-color();
		@include form-input-text-font();
		margin: 0;
		padding-top: 0;
		color: var(--ms-input-placeholder-color);
	}

	.multiselect__input {
		@include form-input-placeholder-color();
		@include form-input-text-font();
		margin: 0;
		padding: 0;
		border-top: none;
		opacity: 0;
		background-color: var(--ms-input-bg);
		color: var(--ms-input-color);

		.multi-select:not(.-select-inline) & {
			opacity: 1;
			position: absolute;
			top: 1px;
			left: 1px;
			width: calc(100% - 2px) !important; // stylelint-disable-line declaration-no-important
			height: 46px;
			border: none;
			border-radius: var(--ms-input-border-radius);
			outline: none;
			padding: 0 0 0 $form-input-padding !important; // stylelint-disable-line declaration-no-important
		}

		.multi-select.-select-inline & {
			border: 1px solid var(--ms-input-border-color-hover); // stylelint-disable-line declaration-no-important
			// border-radius: var(--ms-input-border-radius);
			border-top: none;
		}

		.multiselect--active & {
			opacity: 1;
			position: absolute;
			top: 1px;
			left: 0;
			width: 100%;
			height: $form-input-height;
			border-radius: 0;
			margin: 0;
			padding: 0 0 0 $form-input-padding;
		}

		.multiselect--active.-select-inline & {
			top: $form-input-height - 1px;
		}
	}

	// arrow
	.multiselect__select {
		height: $form-input-height - 2px;
	}

	.multiselect__tags {
		min-height: $form-input-height;
		border-radius: var(--ms-input-border-radius);
		border-color: var(--ms-input-border-color);
		background-color: var(--ms-input-bg);
		display: flex;
		align-items: center;
		padding: 0 $form-input-padding;

		.multiselect--active &,
		&:hover {
			border-color: var(--ms-input-border-color-hover);
			border-radius: var(--ms-input-border-radius);
		}

		.multiselect--active & {
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
		}

		.multiselect--active:not(.-no-search) & {
			border-bottom: none;
		}
	}

	.multiselect__tags-wrap {
		@include ellipsis;
		@include form-input-text-font();
		display: inline-block;
		padding-right: $sp5;
		margin-left: $multi-select-clear-width + $form-input-padding;
	}

	//////////////////////////////
	// Dropdown
	//////////////////////////////

	.multiselect__content-wrapper {
		border-radius: var(--t-form-select-body-border-radius);
		box-shadow: var(--ms-dropdown-box-shadow); // stylelint-disable-line declaration-property-value-allowed-list
		background-color: var(--ms-dropdown-bg);
		border: none;
		margin-top: 0;

		.multi-select:not(.multiselect--above).-select-inline:not(.-no-search) & {
			top: calc(100% + #{$form-input-height});
			margin-top: 0;
		}

		.multiselect--above & {
			margin-top: 0;
			margin-bottom: 7px;
		}

		.multi-select.multiselect--above.-select-inline:not(.-no-search) & {
			bottom: calc(100% + 7px);
			margin-bottom: 0;
			margin-top: 0;
		}
	}

	.multiselect__content {
		max-width: 100%;
	}

	.multiselect__element .multiselect__option {
		@include ellipsis;
		display: inline-block;
		padding: 0 $form-input-padding 0 $multi-select-indent;
		width: 100%;
		min-height: $form-input-height;
		color: var(--ms-option-color) !important; // stylelint-disable-line declaration-no-important
		line-height: $form-input-height;
		background-color: transparent;

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 50%;
			right: auto;
			left: 13px;
			width: 22px;
			height: 22px;
			padding: 0;
			transform: translateY(-50%);
		}

		&::before {
			padding: 4px;
		}

		&::after {
			border: 1px solid var(--ms-option-border-color);
		}

		// nested main-options
		.multiselect.-group-select & {
			&.multiselect__option--group {
				border-top: 1px solid var(--ms-option-group-border-color);
			}
		}

		// nested sub-options
		.multiselect.-group-select & {
			&:not(.multiselect__option--group) {
				padding: 0 $form-input-padding 0 $multi-select-nested-indent;

				&::before,
				&::after {
					left: 21px;
				}
			}
		}

		&--group-selected,
		&--selected {
			background-color: transparent;
			font-weight: 400; // stylelint-disable-line property-disallowed-list

			&::before {
				content: '✓';
				font-family: monospace; // stylelint-disable-line
				display: inline-flex;
				align-items: center;
				justify-content: center;
				color: var(--ms-option-color-checkmark);
				background-color: var(--ms-option-selected-color);
			}

			&::after {
				border: 1px solid var(--ms-option-selected-color);
			}
		}

		&--group-selected {
			&::after {
				border: 1px solid var(--ms-option-selected-color);
			}
		}

		&--highlight {
			background-color: var(--ms-option-bg-hover);

			&::after {
				background-color: transparent;
			}
		}

		&--disabled {
			display: none;
		}
	}
}
</style>
